import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { useAthleteProfile } from "@/hooks/use-athlete-profile";
import { useTodayRecovery } from "@/hooks/use-today-recovery";
import {
  mealSlotCopy,
  type MealType,
  type MealTypeTarget,
  type RecoveryState,
  type DietPattern,
} from "@/lib/meal-targets";
import { SkeletonLoader } from "@/components/system";
import {
  DailyDiaryGauge,
  DailyDiaryGaugeLegend,
} from "@/components/diary/DailyDiaryGauge";
import { DailyDiaryMacros } from "@/components/diary/DailyDiaryMacros";
import { MealLabelChips } from "@/components/diary/MealLabelChips";
import { RecoveryMealBanner } from "@/components/diary/RecoveryMealBanner";
import { deriveMealLabels, type MealLabel } from "@/lib/meal-labels";
import { dailyMealRecommendation } from "@/lib/meal-recommendations";
import { LogMealDialog } from "@/components/today/LogMealDialog";
import { filterImages } from "@/components/today/MealPhotoTab";

type MealRowWithLabels = {
  id: string;
  name: string;
  kcal: number;
  ts: string;
  labels: MealLabel[];
};

/**
 * BW20-B — /log/meals/:date — per-meal slot cards with computed kcal range.
 * The full Lifesum-grade Daily Diary (gauge + macro bars) lands in PR-D.
 *
 * Each slot reads:
 *   - target (from computeMealTypeTargets — pure function)
 *   - logged so far (sum of kcal for meals timestamped inside the slot's
 *     time window, on the user's local day)
 *   - State / Meaning / Next-move line per the adherence-neutral copy
 *     template in CLAUDE.md
 */

const SLOT_ORDER: MealType[] = [
  "breakfast",
  "snack_morning",
  "pre_workout",
  "lunch",
  "snack_afternoon",
  "post_workout",
  "dinner",
  "snack_evening",
];

const SLOT_LABEL: Record<MealType, string> = {
  breakfast: "Breakfast",
  snack_morning: "Morning snack",
  pre_workout: "Pre-workout",
  lunch: "Lunch",
  snack_afternoon: "Afternoon snack",
  post_workout: "Post-workout",
  dinner: "Dinner",
  snack_evening: "Evening snack",
};

// Hour-based bucketing of logged meals into slots. Pre/post workout slots are
// only present if the workout slots were inserted by computeMealTypeTargets;
// otherwise the meals fall into the surrounding main slot.
function bucketMeal(hour: number, slotsPresent: Set<MealType>): MealType {
  if (hour < 5) return "snack_evening";
  if (hour < 10) return "breakfast";
  if (hour < 11) return "snack_morning";
  if (hour < 12 && slotsPresent.has("pre_workout")) return "pre_workout";
  if (hour < 14) return "lunch";
  if (hour < 16 && slotsPresent.has("post_workout")) return "post_workout";
  if (hour < 17) return "snack_afternoon";
  if (hour < 21) return "dinner";
  return "snack_evening";
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function recoveryStateFromScore(score: number | null): RecoveryState {
  if (score == null) return "amber";
  if (score < 40) return "red";
  if (score < 70) return "amber";
  return "green";
}

function rpeToIntensity(rpe: number | null | undefined): "low" | "moderate" | "high" {
  if (rpe == null) return "moderate";
  if (rpe <= 4) return "low";
  if (rpe >= 7) return "high";
  return "moderate";
}

function glp1DoseDayOfWeek(lastTitrationDate: string | null): number | undefined {
  if (!lastTitrationDate) return undefined;
  const last = new Date(lastTitrationDate);
  const now = new Date();
  const days = Math.floor(
    (now.getTime() - last.getTime()) / (1000 * 60 * 60 * 24),
  );
  if (days < 0) return undefined;
  // Cycle within a 7-day dosing pattern (most GLP-1 protocols are weekly).
  return days % 7;
}

export function LogMealsDay() {
  const { date: paramDate } = useParams<{ date?: string }>();
  const date = paramDate ?? todayIso();
  const { session } = useSession();
  const userId = session?.user?.id;
  const profile = useAthleteProfile();
  const recovery = useTodayRecovery();

  const [dropDialogOpen, setDropDialogOpen] = useState(false);
  const [pendingDropFiles, setPendingDropFiles] = useState<File[]>([]);
  const enterCount = useRef(0);
  const [isDropOver, setIsDropOver] = useState(false);

  const qc = useQueryClient();

  const meals = useQuery({
    queryKey: ["log_meals_day", userId, date],
    enabled: Boolean(userId),
    queryFn: async () => {
      if (!userId) return [];
      const startISO = `${date}T00:00:00Z`;
      const endISO = `${date}T23:59:59Z`;
      const { data, error } = await supabase
        .from("meal")
        .select("id, ts, name, kcal, macros, micros")
        .eq("user_id", userId)
        .gte("ts", startISO)
        .lte("ts", endISO)
        .order("ts", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  const sessions = useQuery({
    queryKey: ["log_meals_day_sessions", userId, date],
    enabled: Boolean(userId),
    queryFn: async () => {
      if (!userId) return [];
      const startISO = `${date}T00:00:00Z`;
      const endISO = `${date}T23:59:59Z`;
      const { data, error } = await supabase
        .from("session")
        .select("id, start_ts, end_ts, duration_min, kcal, rpe")
        .eq("user_id", userId)
        .gte("start_ts", startISO)
        .lte("start_ts", endISO);
      if (error) throw error;
      return data ?? [];
    },
  });

  // BW20-C: subscribe to session inserts so the gauge updates live when
  // HAE writes a workout's kcal back.
  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel(`log-meals-day-sessions-${userId}-${date}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "session",
          filter: `user_id=eq.${userId}`,
        },
        () => {
          qc.invalidateQueries({ queryKey: ["log_meals_day_sessions", userId, date] });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, date, qc]);

  const plan = useMemo(() => {
    const p = profile.data;
    if (!p) return null;
    const dailyKcalTarget = p.target_kcal_d ?? p.tdee_kcal ?? 2400;
    const bodyMassKg = p.current_weight_kg ?? 75;
    const dietPattern: DietPattern =
      (p.diet_pattern as DietPattern) ?? "plant_forward";

    // Convert the first session of the day (planned or actual) into a
    // TrainingSession for the recommendation engine. Intensity comes from
    // RPE when available, otherwise default to moderate.
    const firstSession = (sessions.data ?? [])[0];
    const trainingSession = firstSession
      ? {
          starts_at: new Date(firstSession.start_ts),
          duration_min: Number(firstSession.duration_min ?? 60),
          intensity: rpeToIntensity(firstSession.rpe),
        }
      : undefined;

    return dailyMealRecommendation({
      dailyKcalTarget,
      dietPattern,
      bodyMassKg,
      recoveryState: recoveryStateFromScore(recovery.data?.score ?? null),
      trainingSession,
      glp1DoseDayOfWeek: glp1DoseDayOfWeek(p.glp1_last_titration ?? null),
    });
  }, [profile.data, recovery.data, sessions.data]);

  const targets = plan?.targets ?? null;

  if (profile.isLoading || recovery.isLoading || meals.isLoading) {
    return <SkeletonLoader variant="block" count={3} />;
  }

  if (!targets) {
    return (
      <p style={{ color: "var(--fg-3)" }}>
        Set a daily kcal target in Settings to see per-meal ranges.
      </p>
    );
  }

  const profileData = profile.data;
  const dietPattern: DietPattern =
    (profileData?.diet_pattern as DietPattern) ?? "plant_forward";

  const slotsPresent = new Set(Object.keys(targets) as MealType[]);
  const loggedBySlot = new Map<MealType, number>();
  const mealsBySlot = new Map<MealType, MealRowWithLabels[]>();
  let totalConsumed = 0;
  let totalProtein = 0;
  let totalCarbs = 0;
  let totalFat = 0;
  for (const m of meals.data ?? []) {
    const hour = new Date(m.ts).getHours();
    const slot = bucketMeal(hour, slotsPresent);
    const macros = m.macros as { p?: number; c?: number; f?: number } | null;
    const micros =
      (m.micros as Record<string, number | null | undefined>) ?? {};
    const protein = Number(macros?.p ?? 0);
    const carbs = Number(macros?.c ?? 0);
    const fat = Number(macros?.f ?? 0);
    const kcal = Number(m.kcal ?? 0);

    const labels = deriveMealLabels(
      {
        kcal,
        protein_g: protein,
        carbs_g: carbs,
        fat_g: fat,
        fiber_g: micros.fiber_g ?? null,
        iron_mg: micros.iron_mg ?? null,
        omega3_epa_mg: micros.omega3_epa_mg ?? null,
        omega3_dha_mg: micros.omega3_dha_mg ?? null,
        omega3_ala_g: micros.omega3_ala_g ?? null,
        vitamin_b12_ug: micros.vitamin_b12_ug ?? null,
        sodium_mg: micros.sodium_mg ?? null,
      },
      { diet_pattern: dietPattern },
    );

    loggedBySlot.set(slot, (loggedBySlot.get(slot) ?? 0) + kcal);
    if (!mealsBySlot.has(slot)) mealsBySlot.set(slot, []);
    mealsBySlot.get(slot)!.push({
      id: m.id,
      name: m.name,
      kcal,
      ts: m.ts,
      labels,
    });
    totalConsumed += kcal;
    totalProtein += protein;
    totalCarbs += carbs;
    totalFat += fat;
  }
  const totalBurned = (sessions.data ?? []).reduce(
    (acc, s) => acc + Number(s.kcal ?? 0),
    0,
  );

  const dailyKcalTarget = profileData?.target_kcal_d ?? profileData?.tdee_kcal ?? 2400;
  const bodyMassKg = profileData?.current_weight_kg ?? 75;

  // Macro target bands per CLAUDE.md endurance literature defaults:
  // carbs 50-65% kcal, protein 1.6-2.2 g/kg, fat 20-35% kcal.
  const carbBand = {
    min_g: Math.round((dailyKcalTarget * 0.5) / 4),
    max_g: Math.round((dailyKcalTarget * 0.65) / 4),
  };
  const proteinBand = {
    min_g: Math.round(bodyMassKg * 1.6),
    max_g: Math.round(bodyMassKg * 2.2),
  };
  const fatBand = {
    min_g: Math.round((dailyKcalTarget * 0.2) / 9),
    max_g: Math.round((dailyKcalTarget * 0.35) / 9),
  };

  function handleDragEnter(e: React.DragEvent) {
    if (!hasDragFiles(e.dataTransfer)) return;
    e.preventDefault();
    enterCount.current += 1;
    setIsDropOver(true);
  }
  function handleDragOver(e: React.DragEvent) {
    if (!hasDragFiles(e.dataTransfer)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }
  function handleDragLeave() {
    enterCount.current = Math.max(0, enterCount.current - 1);
    if (enterCount.current === 0) setIsDropOver(false);
  }
  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    enterCount.current = 0;
    setIsDropOver(false);
    const files = filterImages(e.dataTransfer.files);
    if (files.length === 0) return;
    setPendingDropFiles(files);
    setDropDialogOpen(true);
  }

  return (
    <>
      <article
        aria-labelledby="log-meals-heading"
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={
          isDropOver
            ? {
                outline: "2px dashed var(--accent)",
                outlineOffset: 4,
                borderRadius: "var(--radius-2)",
                background: "var(--accent-wash)",
              }
            : undefined
        }
      >
        {isDropOver && (
          <div
            aria-live="polite"
            style={{
              padding: "var(--space-3)",
              color: "var(--accent)",
              fontWeight: 500,
              fontSize: "var(--type-body-s)",
              textAlign: "center",
            }}
          >
            Drop image to extract
          </div>
        )}
        <p
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: "var(--type-micro)",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "var(--fg-3)",
            margin: 0,
          }}
        >
          <Link to="/health/nutrition" style={{ color: "var(--fg-3)" }}>
            Nutrition
          </Link>{" "}
          · Diary
        </p>
        <h1
          id="log-meals-heading"
          style={{
            fontFamily: "var(--font-serif)",
            fontStyle: "italic",
            fontWeight: 500,
            fontSize: "var(--type-display-m)",
            margin: "var(--space-2) 0 var(--space-4)",
          }}
        >
          {date === todayIso() ? "Today's meals" : `Meals on ${date}`}
        </h1>

        {plan && (
          <div style={{ marginBottom: "var(--space-4)" }}>
            <RecoveryMealBanner plan={plan} />
          </div>
        )}

        <section
          aria-label="Daily diary summary"
          style={{
            display: "grid",
            gap: "var(--space-4)",
            padding: "var(--space-4)",
            border: "1px solid var(--bg-3)",
            borderRadius: "var(--radius-2)",
            background: "var(--bg-1)",
            marginBottom: "var(--space-6)",
          }}
        >
          <div
            style={{
              display: "grid",
              placeItems: "center",
              gap: "var(--space-3)",
            }}
          >
            <DailyDiaryGauge
              consumed={totalConsumed}
              burned={totalBurned}
              target={dailyKcalTarget}
            />
            <DailyDiaryGaugeLegend
              consumed={totalConsumed}
              burned={totalBurned}
              target={dailyKcalTarget}
            />
          </div>
          <DailyDiaryMacros
            carbs={{ logged_g: totalCarbs, band: carbBand }}
            protein={{ logged_g: totalProtein, band: proteinBand }}
            fat={{ logged_g: totalFat, band: fatBand }}
          />
        </section>

        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          {SLOT_ORDER.filter((s) => targets[s]).map((slot) => (
            <SlotCard
              key={slot}
              slot={slot}
              target={targets[slot]!}
              loggedKcal={loggedBySlot.get(slot) ?? 0}
              meals={mealsBySlot.get(slot) ?? []}
            />
          ))}
        </div>
      </article>

      <LogMealDialog
        open={dropDialogOpen}
        onClose={() => {
          setDropDialogOpen(false);
          setPendingDropFiles([]);
        }}
        initialTab="photo"
        initialFiles={pendingDropFiles}
      />
    </>
  );
}

function SlotCard({
  slot,
  target,
  loggedKcal,
  meals,
}: {
  slot: MealType;
  target: MealTypeTarget;
  loggedKcal: number;
  meals: MealRowWithLabels[];
}) {
  const copy = mealSlotCopy({ mealType: slot, target, loggedKcal });

  // Drift indicator. Green inside range, amber within +/- 20% of bounds, red beyond.
  const lower = target.kcal_min;
  const upper = target.kcal_max;
  const ratio = loggedKcal === 0 ? 0 : loggedKcal / Math.max(1, upper);
  let tone: "neutral" | "ok" | "warn" | "off";
  if (loggedKcal === 0) tone = "neutral";
  else if (loggedKcal >= lower && loggedKcal <= upper) tone = "ok";
  else {
    const drift =
      loggedKcal < lower
        ? (lower - loggedKcal) / Math.max(1, lower)
        : (loggedKcal - upper) / Math.max(1, upper);
    tone = drift <= 0.2 ? "warn" : "off";
  }

  const toneFill: Record<typeof tone, string> = {
    neutral: "var(--fg-4)",
    ok: "var(--green-700, #2f8a3a)",
    warn: "var(--amber-700, #b07b00)",
    off: "var(--brick, #b04a30)",
  };

  return (
    <section
      aria-label={`${SLOT_LABEL[slot]} slot`}
      style={{
        border: "1px solid var(--bg-3)",
        borderRadius: "var(--radius-2)",
        padding: "var(--space-3)",
        background: "var(--bg-1)",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: "var(--space-3)",
        }}
      >
        <h3
          style={{
            fontFamily: "var(--font-sans)",
            fontWeight: 600,
            fontSize: "var(--type-body)",
            margin: 0,
          }}
        >
          {SLOT_LABEL[slot]}
        </h3>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "var(--type-body-s)",
            color: "var(--fg-3)",
          }}
        >
          {target.kcal_min}–{target.kcal_max} kcal
        </span>
      </header>

      <div
        role="img"
        aria-label={`Logged ${Math.round(loggedKcal)} of ${target.kcal_min} to ${target.kcal_max} kcal`}
        style={{
          marginTop: "var(--space-2)",
          height: 6,
          borderRadius: 3,
          background: "var(--bg-2)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            height: "100%",
            width: `${Math.min(100, Math.max(0, ratio * 100))}%`,
            background: toneFill[tone],
            transition: "width 200ms ease-out",
          }}
        />
      </div>

      <p
        style={{
          marginTop: "var(--space-2)",
          marginBottom: 0,
          fontSize: "var(--type-body-s)",
          color: "var(--fg-2)",
          lineHeight: 1.5,
        }}
      >
        <strong style={{ color: "var(--fg-1)" }}>State.</strong> {copy.state}
        <br />
        <strong style={{ color: "var(--fg-1)" }}>Meaning.</strong> {copy.meaning}
        <br />
        <strong style={{ color: "var(--fg-1)" }}>Next move.</strong> {copy.nextMove}
      </p>

      {meals.length > 0 && (
        <ul
          style={{
            marginTop: "var(--space-2)",
            paddingLeft: 0,
            listStyle: "none",
            display: "grid",
            gap: "var(--space-2)",
          }}
        >
          {meals.map((m) => (
            <li key={m.id} style={{ display: "grid", gap: 4 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "var(--type-body-s)",
                  color: "var(--fg-3)",
                }}
              >
                <span>{m.name || "—"}</span>
                <span style={{ fontFamily: "var(--font-mono)" }}>{Math.round(m.kcal)} kcal</span>
              </div>
              {m.labels.length > 0 && <MealLabelChips labels={m.labels} />}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function hasDragFiles(dt: DataTransfer | null): boolean {
  if (!dt) return false;
  return Array.from(dt.types ?? []).includes("Files");
}
